import {ComponentType} from "../_models/component-type";
import {Injectable, OnDestroy} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {ApiService} from "../services/api/api.service";
import {SearchQueryOptions} from "../services/api/search-query-options";
import {map, takeUntil} from "rxjs/operators";
import {ListResponse, SingleResponse} from "../services/api/response-types";
import {GenericDataService, ObjectListByRelationship} from './generic-data.service';
import {
    getManyRelationWithIdsFilter,
    getRelationWithIdFilter, getRelationWithManyIdsFilter
} from "../services/api/filter_utils";
import {IDMap, ModelID} from "../_typing/generic-types";
import {ComponentTypeComponentType} from "../_models/component-type-component-type";

export interface ComponentTypeByRelationshipResponse {
    component_types: ComponentType[],
    ct_dict: { [key: string]: ComponentType },
    ct_list_dict: { [key: string]: string[] }
}

export type UniqueComponentTypeRelationshipDict = IDMap<{
    isFirstComponent: boolean;
    isUnique: boolean;
    isListUnique: boolean
}>

/**
 * This class is for shared functions relating to data access on the component_type api
 **/
@Injectable({
    providedIn: 'root'
})
export class ComponentTypeDataService implements OnDestroy {
    private readonly onDestroy = new Subject<void>();

    constructor(private dataService: GenericDataService,
                private api: ApiService) {
    }

    /**Streams component_types by parent_ids
     * Returns an object of type ComponentTypeByParentResponse
     * @param parent_ids:string[]
     * @param parent_type:string
     */
    streamComponentTypesByRelationshipIds(rel_ids: string[], rel_name: string, ids?: string[], names?: string[]): Observable<ComponentTypeByRelationshipResponse> {
        return this.dataService.streamObjectListByRelationshipIds(rel_ids, rel_name, 'component_type', ids, names,
            null, 'name').pipe(
            map((response: ObjectListByRelationship<ComponentType>) => {
                return {
                    component_types: response?.object_list || [],
                    ct_dict: response?.object_dict || {},
                    ct_list_dict: response?.relationship_object_list_dict || {}
                };
            }));
    }

    getComponentTypesByRelationshipIds(rel_ids: string[], rel_name: string, ids?: ModelID[]): Observable<ListResponse<ComponentType>> {
        const options = new SearchQueryOptions();
        options.filters = [getManyRelationWithIdsFilter(rel_name, rel_ids)];
        if (ids && ids.length > 0) {
            options.filters.push({name: 'id', op: 'in', val: ids});
        }
        return this.api.component_type.searchMany(options);
    }

    getComponentTypes(component_type_ids: string[]): Observable<any> {
        const options = new SearchQueryOptions();
        options.filters = [{name: 'id', op: 'in', val: component_type_ids}];
        return this.api.component_type.searchMany(options);
    }

    getComponentTypesByComponentType(selectedComponentTypeId: ModelID): Observable<ListResponse<ComponentType>> {
        const options = new SearchQueryOptions();
        options.filters = this.getComponentRelationshipFilters(selectedComponentTypeId);
        return this.api.component_type.searchMany(options);
    }

    getComponentTypeComponentTypeRelationships(selectedComponentTypeId: ModelID, relatedComponentTypeIds?: ModelID[]): Observable<UniqueComponentTypeRelationshipDict> {
        return this.getComponentTypeComponentTypes(selectedComponentTypeId, relatedComponentTypeIds).pipe(
            takeUntil(this.onDestroy),
            map(result => {
                let ctCtDict = {};
                const componentTypeComponentTypes = result.data || [];
                componentTypeComponentTypes.forEach(ctCt => {
                    const isFirstComponent = relatedComponentTypeIds?.includes(ctCt.relationships.first_component_type.data.id);
                    const ctColumnId = isFirstComponent ? ctCt.relationships.first_component_type.data.id : ctCt.relationships.second_component_type.data.id;
                    const isUnique = isFirstComponent ? ctCt.attributes.second_is_unique : ctCt.attributes.first_is_unique;
                    const isListUnique = isFirstComponent ? ctCt.attributes.first_is_unique : ctCt.attributes.second_is_unique;

                    ctCtDict[ctColumnId] = {
                        isFirstComponent: isFirstComponent,
                        isUnique: isUnique, //single-select vs multi-select
                        isListUnique: isListUnique //each component of this type can only belong to one component of the table component_type
                    };
                });
                return ctCtDict;
            })
        )
    }


    getComponentTypeComponentTypes(selectedComponentTypeId: ModelID, relatedComponentTypeIds?: ModelID[]): Observable<ListResponse<ComponentTypeComponentType>> {
        const options = new SearchQueryOptions();
        const filter1 =
            {
                and: [
                    getRelationWithIdFilter('first_component_type', selectedComponentTypeId),
                    getRelationWithManyIdsFilter('second_component_type', relatedComponentTypeIds)
                ]
            }
        const filter2 =
            {
                and: [
                    getRelationWithIdFilter('second_component_type', selectedComponentTypeId),
                    getRelationWithManyIdsFilter('first_component_type', relatedComponentTypeIds)
                ]
            }
        options.filters = [{or: [filter1, filter2]}]
        return this.api.component_type_component_type.searchMany(options);
    }

    getComponentRelationshipFilters(selectedComponentTypeId: ModelID): any[] {
        return [{
            name: 'component_type_component_type', op: 'any', val: {
                or: [
                    getRelationWithIdFilter('first_component_type', selectedComponentTypeId),
                    getRelationWithIdFilter('second_component_type', selectedComponentTypeId),
                ]
            }
        },
            {not: {name: 'id', op: 'eq', val: selectedComponentTypeId}}]
    }

    getUniqueComponentTypeRelationshipFilters(componentTypeId: ModelID, firstHasFirstUnique: boolean, firstHasSecondUnique: boolean, secondHasFirstUnique: boolean, secondHasSecondUnique: boolean): any[] {
        let filters = [
            {name:'component_type_component_type', op:'any', val:
            {
            or: [{
                and:
                    [{name: 'first_component_type', op: 'has', val: {name: 'id', op: 'eq', val: componentTypeId}},
                        {name: 'first_is_unique', op: 'eq', val: firstHasFirstUnique},
                        {name: 'second_is_unique', op: 'eq', val: firstHasSecondUnique},
                    ]
            },
                {
                    and:
                        [{name: 'second_component_type', op: 'has', val: {name: 'id', op: 'eq', val: componentTypeId}},
                            {name: 'first_is_unique', op: 'eq', val: secondHasFirstUnique},
                            {name: 'second_is_unique', op: 'eq', val: secondHasSecondUnique},
                        ]
                },

            ]
        }},{not: {name: 'id', op: 'eq', val: componentTypeId}}
        ]
        return filters;
    }

    getComponentType(id: ModelID): Observable<SingleResponse<ComponentType>> {
        return this.dataService.getModelById('component_type', id)
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
